import { useEffect, useMemo, useState, memo } from 'react';
import {
  Button,
  Grid,
  Theme,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import { useTranslation } from 'react-i18next';
import { DataTable, DataTableColumn } from '../../tables';
import {
  FilterOption,
  InventoryType,
  ProductType,
  StandardType,
} from '../../enums';
import notEmpty from '../../utils/notEmpty';
import { Filter, InventoryFilters } from '../../types';
import { Cable } from '../../models';
import { QuoteProductQuantity } from '../QuoteProductsTable/Components';
import { getQuantityErrorMessage } from '../../utils/quote';
import {
  useCompanyCustomParts,
  useCurrentUser,
  useQuotableCables,
  useQuoteMarkets,
} from '../../hooks';
import { CatalogTableProps } from './CatalogTable.props';
import * as Sx from './CatalogTable.styles';

export const CatalogTable = memo(
  ({
    form,
    filters,
    onAddProduct,
    onUpdateProduct,
    setFilters,
    sorting,
    units,
    onLoadProducts,
  }: CatalogTableProps) => {
    const { t } = useTranslation();
    const { canSeeCSA, canSeeUL } = useQuoteMarkets(form);
    const isMobile = useMediaQuery((theme: Theme) =>
      theme.breakpoints.down('sm')
    );
    const currentUser = useCurrentUser();
    const [companyId, setCompanyId] = useState<string>(
      form.item?.companyId || currentUser.companyRef.id
    );
    const [quantities, setQuantities] = useState<{ [key: string]: number }>({});
    const [customPartsFilters, setCustomPartsFilters] = useState<Filter[]>([]);

    useEffect(() => {
      if (!filters || !filters.customPartNumber) {
        setCustomPartsFilters([]);
      } else {
        setCustomPartsFilters([['partNumber', '==', filters.customPartNumber]]);
      }
    }, [filters]);

    useEffect(() => {
      if (form.item?.companyId) {
        setCompanyId(form.item.companyId);
      } else {
        setCompanyId(currentUser.companyRef.id);
      }
    }, [form.item?.companyId, currentUser.companyRef.id]);

    const { list: parts } = useCompanyCustomParts(companyId, {
      filters: customPartsFilters,
    });

    const getStandardTypeFilter = () => {
      if (!canSeeCSA || !canSeeUL) {
        return `${FilterOption.StandardType}=${
          canSeeCSA ? InventoryType.CSA : InventoryType.UL
        }`;
      }
      return null;
    };

    const buildCableIdFilter = (id: string) => `${FilterOption.CableId}=${id}`;

    const buildGeneralFilterList = (filters: InventoryFilters) => {
      const {
        productType,
        productSubType,
        material,
        gauge,
        numberOfConductors,
        voltage: rawVoltage,
        standardType,
      } = filters;
      const voltage = rawVoltage === 'N/A' ? -1 : rawVoltage;

      return [
        `${FilterOption.StandardType}=${standardType}`,
        productType && `${FilterOption.ProductType}=${productType}`,
        productSubType && `${FilterOption.ProductSubType}=${productSubType}`,
        material && `${FilterOption.MaterialType}=${material}`,
        voltage && `${FilterOption.Voltage}=${voltage}`,
        productType !== ProductType.BareGround &&
          numberOfConductors &&
          `${FilterOption.NumberOfConductors}=${numberOfConductors}`,
        gauge && `${FilterOption.Gauge}=${gauge}`,
      ]
        .filter(notEmpty)
        .join(';');
    };

    const queryFilters = useMemo(() => {
      if (!filters) return undefined;

      // Direct part number search or custom part number (no parts found)
      if (
        !filters.searchByCable &&
        (filters.partNumber || (filters.customPartNumber && parts.length === 0))
      ) {
        const filterList = [
          getStandardTypeFilter(),
          filters.customPartNumber
            ? buildCableIdFilter(filters.customPartNumber)
            : filters.partNumber
            ? buildCableIdFilter(filters.partNumber)
            : null,
        ]
          .filter(notEmpty)
          .join(';');

        return [['filters', 'array-contains', filterList]] as Filter[];
      }

      // Custom part number search with parts found
      if (!filters.searchByCable && filters.customPartNumber) {
        const filterList = parts.map((part) =>
          [getStandardTypeFilter(), buildCableIdFilter(part.normalizedId)]
            .filter(notEmpty)
            .join(';')
        );

        return [['filters', 'array-contains-any', filterList]] as Filter[];
      }

      // General filter case
      const generalFilterList = buildGeneralFilterList(filters);

      return [['filters', 'array-contains', generalFilterList]] as Filter[];
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters, parts]);

    const { list: cables, loading } = useQuotableCables({
      filters: queryFilters,
      sorting,
    });

    useEffect(() => {
      if (filters && !filters.searchByCable && !loading && cables) {
        setFilters({
          ...filters,
          productType: cables.length > 0 ? cables[0].productType : null,
        });
        if (cables.length > 0) {
          onLoadProducts(
            cables[0].standardType === StandardType.CSA ? 'csa' : 'ul'
          );
        }
      }
      // This component is only rendered if filters is not null, therefore:
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters?.productType, cables, loading]);

    const cableDescription = (description: string, item: Cable) => {
      if (item.colors) {
        return (
          <>
            <div>
              {description} - {item.id}
            </div>
            <div>{item.colors}</div>
          </>
        );
      }

      return (
        <>
          {description} - {item.id}
        </>
      );
    };

    return (
      <DataTable
        data={queryFilters && !loading ? cables : []}
        loading={loading}
        sorting={sorting}
        tableStyles={Sx.tableStyles}
        tableHeaderStyles={Sx.tableHeaderStyles}
        tableBodyStyles={Sx.tableBodyStyles}
        disableActiveItem
      >
        <DataTableColumn
          key="description-column"
          property="description"
          title={t('catalog.fields.partNumber')}
          mobileOrder={0}
          mobileWidth="60%"
          mobileHeight={100}
          output={(description, item: Cable) =>
            cableDescription(description, item)
          }
          otherStyling={{
            wordBreak: 'break-word',
            px: {
              xs: 1.5,
              md: 2,
            },
            pt: {
              xs: '0.625rem',
            },
          }}
        />
        {!!form?.item && (
          <DataTableColumn
            mobileOrder={1}
            mobileWidth="40%"
            mobileHeight={100}
            property="id"
            disableSort
            title={`${t('quotes.edit.products.table.labels.quantity')}*`}
            otherStyling={{
              backgroundColor: 'white',
              my: {
                xs: 'auto',
              },
              px: {
                xs: 0,
                md: 2,
              },
            }}
            output={(_, item: Cable) => {
              const addedProduct = form?.item?.products?.find(
                (p) => p.partNumber === item.id
              );

              return (
                <QuoteProductQuantity
                  showError={false}
                  quantity={
                    addedProduct?.quantity ?? (quantities[item.id] || 0)
                  }
                  onChange={(val) => {
                    const quantity = val ? Number(val) : 0;
                    if (addedProduct) {
                      onUpdateProduct(item, quantity);
                    } else {
                      setQuantities((prev) => {
                        return { ...prev, [item.id]: quantity };
                      });
                    }
                  }}
                  inputAdornment={t(`units.${units}.length`)}
                  width="140px"
                  numberValidation={(val) =>
                    getQuantityErrorMessage(val, item, units)
                  }
                  thousandSeparator
                />
              );
            }}
          />
        )}
        {!!form?.item && (
          <DataTableColumn
            key="add-column"
            property="action"
            title=""
            disableSort
            mobileWidth="100%"
            mobileHeight={50}
            mobileOrder={2}
            textAlign="right"
            mobileTextAlign="center"
            output={(_, item: Cable) => {
              if (
                !form?.item?.products?.find((p) => p.partNumber === item.id)
              ) {
                return (
                  <Tooltip
                    placement="top"
                    title={
                      !quantities[item.id]
                        ? t('catalog.searchDialog.requiredQuantity')
                        : undefined
                    }
                  >
                    <span
                      style={{
                        display: 'inline-block',
                        ...(isMobile ? { width: '100%' } : {}),
                      }}
                    >
                      <Button
                        fullWidth
                        disabled={
                          !quantities[item.id] ||
                          !!getQuantityErrorMessage(
                            quantities[item.id],
                            item,
                            units
                          )
                        }
                        variant="outlined"
                        color="primary"
                        sx={{ display: 'inline-block' }}
                        onClick={() =>
                          onAddProduct(item, quantities[item.id] || 0)
                        }
                      >
                        {t('catalog.searchDialog.addButton')}
                      </Button>
                    </span>
                  </Tooltip>
                );
              } else {
                return (
                  <Grid
                    container
                    alignItems="center"
                    direction="row"
                    justifyContent="flex-end"
                  >
                    <Typography variant="body2" color="secondary" mr={1}>
                      {t('catalog.searchDialog.productAdded')}{' '}
                    </Typography>
                    <CheckCircleRoundedIcon color="secondary" />
                  </Grid>
                );
              }
            }}
          />
        )}
      </DataTable>
    );
  },
  (oldProps, newProps) =>
    oldProps.filters === newProps.filters &&
    oldProps.form.item === newProps.form.item
);
